/**
 * Pelgo News content (company posts + press / media hits).
 *
 * How to add an article (non-engineer / LLM-friendly):
 * 1. Add hero image under `public/assets/news/<slug>-hero.png` (or .jpg/.webp).
 * 2. Add outlet logo under `public/assets/logos/` if needed (reuse existing when possible).
 * 3. Append an object to `newsArticles` below with a unique `slug`.
 * 4. Set `category` to `'company'` or `'press'`.
 * 5. For media hits, set `sourceUrl` (and usually `bodyParagraphs` or leave empty for summary-only).
 * 6. Set `featured: true` on at most one item to pin it on the /news index hero.
 * 7. Open a PR; deploy picks up the new `/news/<slug>` page automatically (SEO/prerender loop over this list).
 */

export type NewsCategory = 'company' | 'press'

export const NEWS_CATEGORY_LABELS: Record<NewsCategory, string> = {
  company: 'Company',
  press: 'Press',
}

export type NewsArticle = {
  slug: string
  title: string
  summary: string
  /** Byline, e.g. "By Spencer Soper" or "Pelgo" */
  author: string
  /** Human-readable date shown in the UI */
  displayDate: string
  /** ISO 8601 date for structured data and sorting */
  publishedAt: string
  category: NewsCategory
  publicationName: string
  outletLogoSrc?: string
  sourceUrl?: string
  sourcePublishedLabel?: string
  /** Optional note under the source link (paywall, video, etc.) */
  sourceNote?: string
  heroImageSrc: string
  heroImageAlt: string
  /** Omit or leave empty for title + summary + outbound link only */
  bodyParagraphs?: readonly string[]
  /** Optional italic callout above the body (e.g. newsletter intro) */
  introCallout?: string | null
  sectionHeading?: string
  publicationSeries?: string
  tags?: readonly string[]
  featured?: boolean
}

/** @deprecated Use NewsArticle */
export type PressArticle = NewsArticle

const BLOOMBERG_SOURCE_URL =
  'https://www.bloomberg.com/news/newsletters/2026-04-17/startup-pelgo-offers-ai-to-help-find-new-jobs-for-workers-laid-off-by-ai'

const CNBC_SOURCE_URL =
  'https://www.cnbc.com/video/2026/10/09/pelgo-ceo-chieh-huang-on-retraining-workers-for-an-ai-future.html'

const NYT_SOURCE_URL =
  'https://www.nytimes.com/2026/10/02/business/economy/jobs-report-unemployment.html'

const BUSINESSWIRE_SOURCE_URL =
  'https://www.businesswire.com/news/home/20260603021073/en/AI-Is-Fueling-Alternative-Career-Paths-Only-1-in-5-Unemployed-Workers-Say-Theyd-Choose-a-4-Year-Degree-Now-New-Poll-Shows'

export const newsArticles: readonly NewsArticle[] = [
  {
    slug: 'cnbc-retraining-workers-ai-future',
    title: 'Pelgo CEO Chieh Huang on retraining workers for an AI future',
    summary:
      "Chieh Huang, Pelgo founder and CEO, joins CNBC's Squawk Box to discuss the AI layoff business, how Pelgo plans to retrain workers laid off due to AI, and more.",
    author: 'CNBC Squawk Box',
    displayDate: 'October 9, 2026 at 8:09 AM EDT',
    publishedAt: '2026-10-09',
    category: 'press',
    publicationName: 'CNBC',
    outletLogoSrc: '/assets/logos/cnbc.png',
    sourceUrl: CNBC_SOURCE_URL,
    sourcePublishedLabel: 'October 9, 2026',
    sourceNote: 'Watch the full Squawk Box interview on CNBC.',
    publicationSeries: 'Squawk Box',
    // Summary-only: video interview — no transcript republished.
    bodyParagraphs: [
      "Pelgo founder and CEO Chieh Huang appeared on CNBC's Squawk Box to talk about workforce disruption from artificial intelligence and how employers can support people whose roles are changing or disappearing.",
      'In the interview, Huang discusses Pelgo’s approach to outplacement and retraining — using AI-powered tools to help displaced workers prepare for new roles faster and at lower cost than traditional career transition programs.',
      'The segment covers the emerging “AI layoff” dynamic and Pelgo’s mission to make transition support available more broadly, not only to senior employees who historically received outplacement.',
    ],
    heroImageSrc: '/assets/news/cnbc-retraining-workers-ai-future-hero.jpg',
    heroImageAlt: 'CNBC Squawk Box interview with Pelgo CEO Chieh Huang',
    tags: ['cnbc', 'squawk-box', 'video', 'ai', 'retraining'],
    featured: true,
  },
  {
    slug: 'nyt-labor-market-lower-gear',
    title: 'U.S. Labor Market Shifts to a Lower Gear',
    summary:
      'The economy added fewer jobs in September and unemployment ticked up, while inflation maintained pressure on markets and raised costs. Pelgo CEO Chieh Huang is quoted on job-seeker uncertainty in a low-hire environment.',
    author: 'By Talmon Joseph Smith',
    displayDate: 'October 2, 2026',
    publishedAt: '2026-10-02',
    category: 'press',
    publicationName: 'The New York Times',
    outletLogoSrc: '/assets/logos/new-york-times.png',
    sourceUrl: NYT_SOURCE_URL,
    sourcePublishedLabel: 'October 2, 2026',
    sourceNote: 'The full article may require a New York Times subscription.',
    bodyParagraphs: [
      'In its coverage of the September employment report, The New York Times described a U.S. labor market that “shifts to a lower gear”: employers added 29,000 jobs and the unemployment rate rose to 4.2 percent, slower growth than many expected after recent months of resilience.',
      'Pelgo CEO Chieh Huang is quoted on what that softness looks like for people searching for work. He said the “low-fire, low-hire” catchphrase has merit, but that there is real angst beneath the surface — including uncertainty about what AI will do to future roles.',
      '“There’s just so much uncertainty in general,” Huang said. “And then so much uncertainty of what A.I. can do in the future so that when you talk to job seekers, like we do constantly, they are finding that hey, it takes a little bit longer these days to find your next gig.”',
      'Huang also noted that a substantial share of people without jobs are long-term unemployed seeking comparable pay or stature, while others take lower-paying roles rather than risk weeks or months without a paycheck.',
    ],
    heroImageSrc: '/assets/news/nyt-labor-market-lower-gear-hero.png',
    heroImageAlt: 'New York Times graphic accompanying coverage of the September 2026 jobs report',
    tags: ['nyt', 'jobs-report', 'labor-market', 'ai'],
  },
  {
    slug: 'businesswire-ai-alternative-career-paths',
    title:
      'AI Is Fueling Alternative Career Paths: Only 1 in 5 Unemployed Workers Say They’d Choose a 4-Year Degree Now',
    summary:
      'Pelgo survey of 5,000 unemployed individuals finds workers rethinking traditional education and career paths as AI skills become the new normal.',
    author: 'Pelgo',
    displayDate: 'June 3, 2026',
    publishedAt: '2026-06-03',
    category: 'company',
    publicationName: 'Business Wire',
    outletLogoSrc: '/assets/logos/businesswire.png',
    sourceUrl: BUSINESSWIRE_SOURCE_URL,
    sourcePublishedLabel: 'June 3, 2026',
    sourceNote: 'Read the full press release on Business Wire.',
    bodyParagraphs: [
      'Pelgo released additional findings from a Talker Research survey of 5,000 unemployed individuals, offering a real-time look at how displaced workers are navigating today’s job market.',
      'Just 20% of respondents say they would apply to a four-year college if they were 18 and deciding again today. Ten percent would choose a two-year school to enter the workforce sooner; 25% would start a business; and 17% would attend trade school instead of a four-year college.',
      'More than half (57%) say learning to use AI on the job will be the new normal, while only 44% feel somewhat or very confident that their current skills are competitive.',
      '“Traditional outplacement hasn’t kept pace with how quickly the workforce is changing, especially with the rise of AI and if entire industries disappear,” said Pelgo CEO Chieh Huang. “People are not short on effort, they’re short on clarity.”',
      'Pelgo is using the insights to inform tools and services that help people transition into in-demand roles with a mix of intelligent technology and human support.',
    ],
    heroImageSrc: '/assets/news/businesswire-ai-alternative-career-paths-hero.jpg',
    heroImageAlt:
      'Professional writing on a clipboard outdoors, representing career transition and job search',
    tags: ['company', 'survey', 'ai', 'career-paths', 'businesswire'],
  },
  {
    slug: 'bloomberg-searching-for-work',
    title: 'Startup Offers AI Tools to Find New Jobs for Workers Displaced by AI',
    summary: 'Pelgo says it can provide placement services quicker and cheaper using AI',
    author: 'By Spencer Soper',
    displayDate: 'April 17, 2026 at 7:02 AM EDT',
    publishedAt: '2026-04-17',
    category: 'press',
    publicationName: 'Bloomberg',
    outletLogoSrc: '/assets/logos/bloomberg.png',
    sourceUrl: BLOOMBERG_SOURCE_URL,
    sourcePublishedLabel: 'April 17, 2026',
    sourceNote: 'The full article may require a Bloomberg subscription.',
    publicationSeries: 'Tech In Depth',
    introCallout:
      'Welcome to Tech In Depth, our daily newsletter about the business of tech from Bloomberg’s journalists around the world. Today, Spencer Soper reports on a startup that says AI can help workers who lose their jobs — because of AI.',
    sectionHeading: 'Searching for work',
    bodyParagraphs: [
      'Chieh Huang learned a lot after launching Boxed Wholesale from his parents’ New Jersey garage. Over the course of a decade, he built a highly automated warehouse, ramped to a peak of about 600 employees, went public on the New York Stock Exchange and ultimately tapped out with a 2023 bankruptcy filing. His once promising operation, which was often described as Costco in your smartphone because it specialized in bulk sizes of household goods ordered through an app, got sold off in pieces.',
      'Now the tech founder who cut hundreds of jobs — and has been laid off himself — is trying to convert those lessons into a comeback story. Huang thinks artificial intelligence, which threatens to displace millions of jobs globally, can make layoffs more humane and help displaced workers quickly find new roles.',
      'So AI is likely coming for your job. But it also might be coming to the rescue.',
      '“What we realized is no one has created a company to help all the people being displaced by AI,” said Huang, co-founder and chief executive officer at Pelgo, which is based in New York City. “Who is helping them transition to the future if their industry starts to disappear?”',
      'Tech company restructurings tied to artificial intelligence were a key driver of 1.2 million layoffs announced in the US last year, according to a January report by career transition firm Challenger, Gray & Christmas.',
      'With $5.5 million from investors, Pelgo is targeting the employer-sponsored outplacement industry, which globally generates about $5 billion annually to help displaced workers freshen their resumes and LinkedIn profiles and otherwise guide them toward new careers. The outplacement firms are typically hired by the employer that is shedding workers, at a cost of several thousand dollars per employee. Huang said Pelgo can use AI tools to reduce that expense to about $20 per month per displaced worker, which he thinks will compel more employers to offer the services.',
      'Outplacement services are required in some countries, like France and Germany. In the US, they’re voluntary. Many employers offer them as a hedge against wrongful termination claims and to protect their reputations.',
      'Huang said the voluntary nature of outplacement in the US means many employees who need it most, like front-line workers in retail, warehousing and hospitality, don’t get it at all. Retail and warehousing combined for more than 180,000 job cuts in 2025, with both industries seeing increased displacement from the previous year, according to the Challenger, Gray & Christmas report.',
      'Pelgo uses large language models to scan worker resumes and make suggestions to help them stand out for the software tools recruiters often use to screen applicants. It also offers advice about what kind of training would help them qualify for new roles.',
      'One-third of 5,000 unemployed workers surveyed by Pelgo said they’ve been job searching for six months or longer, indicating a lot of people could use help finding a new paycheck. Huang, who declined to name any existing customers, said the service is live in seven countries and that hundreds of displaced workers have used it. Their marketing relies mostly on word of mouth through corporate human resources circles.',
      '“Outplacement was always a service offered to people who needed it the least,” he said. “We want it to be for everyone.”',
    ],
    heroImageSrc: '/assets/news/bloomberg-searching-for-work-hero.png',
    heroImageAlt:
      'Outdoor hiring event sign on a sidewalk in front of a building, with hiring event flyer attached',
    tags: ['bloomberg', 'outplacement', 'ai'],
  },
] as const

/** @deprecated Use newsArticles */
export const pressArticles = newsArticles

export function getNewsPath(slug: string): string {
  return `/news/${slug}`
}

export function listNewsArticles(): readonly NewsArticle[] {
  return [...newsArticles].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))
}

export function listFeaturedNewsArticles(): readonly NewsArticle[] {
  return listNewsArticles().filter((article) => article.featured)
}

export function getNewsArticleBySlug(slug: string): NewsArticle | undefined {
  return newsArticles.find((article) => article.slug === slug)
}

/** @deprecated Use getNewsArticleBySlug */
export function getPressArticleBySlug(slug: string): NewsArticle | undefined {
  return getNewsArticleBySlug(slug)
}
